import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { Container, Grid, Box, Typography, Button, Paper, IconButton } from '@mui/material';
import Header from './components/Header';
import CurrencyCard from './components/CurrencyCard';
import CurrencySkeleton from './components/CurrencySkeleton';
import MarketOverview from './components/MarketOverview';
import ControlsBar from './components/ControlsBar';
import ConverterModal from './components/ConverterModal';
import {
  SearchOffRounded as SearchOffRoundedIcon,
  CheckRounded as CheckRoundedIcon,
  RestartAltRounded as RestartAltRoundedIcon,
  ArrowBackRounded as ArrowBackRoundedIcon,
  ArrowForwardRounded as ArrowForwardRoundedIcon,
} from '@mui/icons-material';
import { getTranslation, getCurrencyFullName, toPersianDigits } from './i18n';
import { triggerHaptic, triggerNotificationHaptic } from './utils/nativeBridge';
import { updateWidgetRates } from './utils/widgetSync';

const App = ({ toggleTheme, language = 'fa', toggleLanguage, t: customT }) => {
  const t = customT || ((key, params) => getTranslation(language, key, params));
  const [currencies, setCurrencies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [time, setTime] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState(0); // 0: All, 1: Currencies, 2: Gold
  const [converterOpen, setConverterOpen] = useState(false);
  const [activeDragCode, setActiveDragCode] = useState(null);
  const [isReorderMode, setIsReorderMode] = useState(false);
  const [selectedReorderTile, setSelectedReorderTile] = useState(null);
  const [selectedCurrency, setSelectedCurrency] = useState(() => {
    return localStorage.getItem('selectedWidgetCurrency') || 'USD';
  });

  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const tabParam = params.get('tab');
      if (tabParam === 'currencies' || tabParam === 'currency') setActiveTab(1);
      else if (tabParam === 'gold') setActiveTab(2);

      if (params.get('action') === 'converter') {
        const curParam = params.get('currency');
        if (curParam) setSelectedCurrency(curParam.toUpperCase());
        setConverterOpen(true);
      }
    } catch (e) {
      // Graceful fallback
    }
  }, []);

  const handleOpenConverter = (shortCode) => {
    triggerHaptic('light');
    const code = shortCode || selectedCurrency || 'USD';
    if (code && typeof code === 'string') {
      setSelectedCurrency(code.toUpperCase());
    }
    setConverterOpen(true);
  };

  // Exit reorder mode if user initiates a search query
  useEffect(() => {
    if (searchQuery.trim()) {
      setIsReorderMode(false);
      setSelectedReorderTile(null);
    }
  }, [searchQuery]);

  const handleToggleReorderMode = () => {
    setIsReorderMode((prev) => !prev);
    setActiveDragCode(null);
    setSelectedReorderTile(null);
  };

  const handleEnterReorderMode = (code) => {
    setIsReorderMode(true);
    if (code) {
      setSelectedReorderTile(code);
    }
  };

  const handleExitReorderMode = () => {
    setIsReorderMode(false);
    setActiveDragCode(null);
    setSelectedReorderTile(null);
    triggerHaptic('light');
  };

  const handleSelectTile = useCallback((code) => {
    setSelectedReorderTile((prev) => (prev === code ? null : code));
  }, []);

  const handleMoveStep = useCallback((code, direction) => {
    if (!code || !direction) return;
    setCurrencies((prevList) => {
      const currIdx = prevList.findIndex((c) => c.short === code);
      if (currIdx === -1) return prevList;
      const targetIdx = currIdx + direction;
      if (targetIdx < 0 || targetIdx >= prevList.length) return prevList;

      const updated = [...prevList];
      const [movedItem] = updated.splice(currIdx, 1);
      updated.splice(targetIdx, 0, movedItem);

      try {
        const orderCodes = updated.map((c) => c.short);
        localStorage.setItem('arzgo_currency_order', JSON.stringify(orderCodes));
      } catch (e) {}

      triggerHaptic('medium');
      return updated;
    });
  }, []);

  const handleResetOrder = () => {
    try {
      localStorage.removeItem('arzgo_currency_order');
    } catch (e) {}
    triggerNotificationHaptic('warning');
    setIsReorderMode(false);
    setActiveDragCode(null);
    setSelectedReorderTile(null);
    fetchCurrencyData(false);
  };

  const handleReorder = useCallback((sourceCode, targetCode) => {
    if (!sourceCode || !targetCode || sourceCode === targetCode) return;
    setCurrencies((prevList) => {
      const sourceIdx = prevList.findIndex((c) => c.short === sourceCode);
      const targetIdx = prevList.findIndex((c) => c.short === targetCode);
      if (sourceIdx === -1 || targetIdx === -1) return prevList;

      const updated = [...prevList];
      const [movedItem] = updated.splice(sourceIdx, 1);
      updated.splice(targetIdx, 0, movedItem);

      try {
        const orderCodes = updated.map((c) => c.short);
        localStorage.setItem('arzgo_currency_order', JSON.stringify(orderCodes));
      } catch (e) {}

      triggerHaptic('light');
      return updated;
    });
  }, []);

  const fetchCurrencyData = useCallback((isManual = false) => {
    if (isManual) setIsRefreshing(true);

    const apiUrl = 'https://ahmand.online/arzgo/main/api/Request.php';
    const formdata = new FormData();
    formdata.append("token", "VkO391ymfubNpvnd8Zu6");
    formdata.append("lang", language);

    fetch(apiUrl, {
      method: 'POST',
      body: formdata,
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        return response.json();
      })
      .then((data) => {
        const cny = (data.cny || []).map((currency) => {
          const upperShort = currency.short ? currency.short.toUpperCase() : '';
          const rawSell = currency.sell ? currency.sell.toLocaleString() : '-';
          const rawBuy = currency.buy ? currency.buy.toLocaleString() : '-';
          return {
            short: upperShort,
            full: getCurrencyFullName(language, currency.short, currency.full),
            sell: language === 'fa' ? toPersianDigits(rawSell) : rawSell,
            buy: language === 'fa' ? toPersianDigits(rawBuy) : rawBuy,
            flag: currency.flag || '🌐',
            type: 'currency',
          };
        });
        const golds = (data.golds || []).map((currency) => {
          const upperShort = currency.short ? currency.short.toUpperCase() : '';
          const rawPrice = currency.price ? currency.price.toLocaleString() : '-';
          return {
            short: upperShort,
            full: getCurrencyFullName(language, currency.short, currency.name),
            sell: language === 'fa' ? toPersianDigits(rawPrice) : rawPrice,
            buy: '‌',
            flag: '🪙',
            type: 'gold',
          };
        });

        const rawList = [...cny, ...golds];

        // Apply saved custom order if present
        let savedOrder = [];
        try {
          savedOrder = JSON.parse(localStorage.getItem('arzgo_currency_order') || '[]');
        } catch (e) {}

        if (Array.isArray(savedOrder) && savedOrder.length > 0) {
          rawList.sort((a, b) => {
            const idxA = savedOrder.indexOf(a.short);
            const idxB = savedOrder.indexOf(b.short);
            if (idxA !== -1 && idxB !== -1) return idxA - idxB;
            if (idxA !== -1) return -1;
            if (idxB !== -1) return 1;
            return 0;
          });
        }

        setCurrencies(rawList);
        const rawTime = data.time || new Date().toLocaleTimeString();
        setTime(language === 'fa' ? toPersianDigits(rawTime) : rawTime);

        // Keep the home-screen widget in sync (rates in raw English digits)
        const widgetRates = [
          ...(data.cny || []).map((c) => ({
            code: (c.short || '').toUpperCase(),
            title: getCurrencyFullName('fa', c.short, c.full),
            sell: c.sell != null ? String(c.sell) : '-',
            buy: c.buy != null ? String(c.buy) : '',
            isGold: false,
            emoji: c.flag || '🌐',
          })),
          ...(data.golds || []).map((g) => ({
            code: (g.short || '').toUpperCase(),
            title: getCurrencyFullName('fa', g.short, g.name),
            sell: g.price != null ? String(g.price) : '-',
            buy: '',
            isGold: true,
            emoji: '🪙',
          })),
        ];
        updateWidgetRates(widgetRates, data.time || '');
        if (isManual) {
          triggerNotificationHaptic('success');
        }
      })
      .catch((error) => {
        console.error('Error fetching currency data:', error);
        if (isManual) {
          triggerNotificationHaptic('error');
        }
      })
      .finally(() => {
        setLoading(false);
        setIsRefreshing(false);
      });
  }, [language]);

  useEffect(() => {
    fetchCurrencyData();
    const intervalId = setInterval(() => fetchCurrencyData(false), 60000);
    return () => clearInterval(intervalId);
  }, [fetchCurrencyData]);

  // Filtered currencies based on search & tab selection
  const filteredCurrencies = useMemo(() => {
    return currencies.filter((item) => {
      // Tab filter
      if (activeTab === 1 && item.type !== 'currency') return false;
      if (activeTab === 2 && item.type !== 'gold') return false;

      // Search query filter
      if (!searchQuery.trim()) return true;
      const q = searchQuery.toLowerCase().trim();
      return (
        (item.short && item.short.toLowerCase().includes(q)) ||
        (item.full && item.full.toLowerCase().includes(q))
      );
    });
  }, [currencies, activeTab, searchQuery]);

  return (
    <Box sx={{ minHeight: '100vh', pb: 6 }}>
      <Header
        toggleTheme={toggleTheme}
        language={language}
        toggleLanguage={toggleLanguage}
        t={t}
      />

      <Container maxWidth="lg" sx={{ mt: { xs: 2, sm: 3.5 }, mb: 4 }}>
        {/* Top Market Summary Banner */}
        <MarketOverview
          totalItems={currencies.length}
          time={time}
          isRefreshing={isRefreshing}
          t={t}
        />

        {/* Filter Controls & Search */}
        <ControlsBar
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          onOpenConverter={() => handleOpenConverter('USD')}
          onRefresh={() => fetchCurrencyData(true)}
          isRefreshing={isRefreshing}
          isReorderMode={isReorderMode}
          onToggleReorderMode={handleToggleReorderMode}
          t={t}
        />

        {/* Content Section */}
        {loading ? (
          <Grid container spacing={{ xs: 1.5, sm: 2 }}>
            {Array.from({ length: 8 }).map((_, index) => (
              <Grid item xs={6} sm={6} md={4} lg={3} key={index}>
                <CurrencySkeleton />
              </Grid>
            ))}
          </Grid>
        ) : filteredCurrencies.length === 0 ? (
          <Paper
            elevation={0}
            sx={{
              p: 6,
              textAlign: 'center',
              borderRadius: 4,
              border: '1px solid',
              borderColor: 'divider',
              mt: 2,
            }}
          >
            <SearchOffRoundedIcon sx={{ fontSize: 56, color: 'text.secondary', mb: 1 }} />
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
              {t('noQuotesFound')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t('noQuotesDesc', { query: searchQuery })}
            </Typography>
            <Button
              variant="outlined"
              size="small"
              onClick={() => {
                setSearchQuery('');
                setActiveTab(0);
              }}
              sx={{
                borderRadius: 2.5,
                height: 36,
                px: 2,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <span style={{ display: 'inline-flex', alignItems: 'center', lineHeight: 1 }}>{t('clearFilters')}</span>
            </Button>
          </Paper>
        ) : (
          <Grid container spacing={{ xs: 1.5, sm: 2 }}>
            {filteredCurrencies.map((item, index) => (
              <Grid item xs={6} sm={6} md={4} lg={3} key={item.short || index}>
                <CurrencyCard
                  {...item}
                  onSelect={() => handleOpenConverter(item.short)}
                  onReorder={handleReorder}
                  onDragStart={(code) => setActiveDragCode(code)}
                  onDragEnd={() => setActiveDragCode(null)}
                  isDragged={activeDragCode === item.short}
                  isReorderMode={isReorderMode}
                  onEnterReorderMode={handleEnterReorderMode}
                  isSelected={selectedReorderTile === item.short}
                  onSelectTile={handleSelectTile}
                  onMoveStep={handleMoveStep}
                  isFirst={index === 0}
                  isLast={index === filteredCurrencies.length - 1}
                  isReorderDisabled={Boolean(searchQuery.trim())}
                  t={t}
                />
              </Grid>
            ))}
          </Grid>
        )}
      </Container>

      {/* Floating Reorder Mode Toolbar */}
      {isReorderMode && (
        <Box
          sx={{
            position: 'fixed',
            bottom: { xs: 'calc(env(safe-area-inset-bottom, 16px) + 12px)', sm: 28 },
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 1300,
            width: { xs: 'calc(100% - 32px)', sm: 'auto' },
            maxWidth: { xs: 440, sm: 'none' },
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1.5,
            p: { xs: 1.75, sm: 1.25 },
            borderRadius: { xs: 4, sm: 50 },
            backgroundColor: (theme) =>
              theme.palette.mode === 'dark' ? 'rgba(17, 24, 39, 0.94)' : 'rgba(255, 255, 255, 0.94)',
            backdropFilter: 'blur(24px)',
            border: '1px solid',
            borderColor: (theme) =>
              theme.palette.mode === 'dark' ? 'rgba(59, 130, 246, 0.4)' : 'rgba(37, 99, 235, 0.3)',
            boxShadow: (theme) =>
              theme.palette.mode === 'dark'
                ? '0 16px 40px rgba(0, 0, 0, 0.7), 0 0 0 1px rgba(255, 255, 255, 0.08)'
                : '0 16px 40px rgba(37, 99, 235, 0.22)',
            animation: 'slideUpBar 0.25s cubic-bezier(0.16, 1, 0.3, 1)',
            '@keyframes slideUpBar': {
              from: { transform: 'translate(-50%, 50px)', opacity: 0 },
              to: { transform: 'translate(-50%, 0)', opacity: 1 },
            },
          }}
        >
          {/* Status Badge & Title */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: { xs: 'space-between', sm: 'flex-start' },
              width: { xs: '100%', sm: 'auto' },
              gap: 1.25,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  backgroundColor: '#3B82F6',
                  boxShadow: '0 0 8px #3B82F6',
                  flexShrink: 0,
                }}
              />
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 800,
                  fontSize: { xs: '0.85rem', sm: '0.875rem' },
                  color: 'text.primary',
                  whiteSpace: 'nowrap',
                }}
              >
                {selectedReorderTile
                  ? t('selectedTile', { code: selectedReorderTile })
                  : t('reorderModeActive')}
              </Typography>
            </Box>

            {/* Quick Step Move Controls for Selected Tile */}
            {selectedReorderTile && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  bgcolor: (theme) =>
                    theme.palette.mode === 'dark' ? 'rgba(59, 130, 246, 0.2)' : 'rgba(37, 99, 235, 0.12)',
                  px: 1,
                  py: 0.25,
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: (theme) =>
                    theme.palette.mode === 'dark' ? 'rgba(59, 130, 246, 0.35)' : 'rgba(37, 99, 235, 0.25)',
                }}
              >
                <IconButton
                  size="small"
                  onClick={() => handleMoveStep(selectedReorderTile, -1)}
                  title={t('movePrev')}
                  sx={{ width: 26, height: 26, color: 'primary.main', p: 0 }}
                >
                  <ArrowBackRoundedIcon sx={{ fontSize: 17 }} />
                </IconButton>
                <Typography
                  variant="caption"
                  sx={{ fontWeight: 800, color: 'primary.main', fontSize: '0.75rem', px: 0.5 }}
                >
                  {selectedReorderTile}
                </Typography>
                <IconButton
                  size="small"
                  onClick={() => handleMoveStep(selectedReorderTile, 1)}
                  title={t('moveNext')}
                  sx={{ width: 26, height: 26, color: 'primary.main', p: 0 }}
                >
                  <ArrowForwardRoundedIcon sx={{ fontSize: 17 }} />
                </IconButton>
              </Box>
            )}
          </Box>

          {/* Action Buttons Row */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              width: { xs: '100%', sm: 'auto' },
            }}
          >
            {/* Done Button */}
            <Button
              variant="contained"
              color="primary"
              size="small"
              fullWidth
              startIcon={<CheckRoundedIcon />}
              onClick={handleExitReorderMode}
              sx={{
                flex: { xs: 1, sm: 'initial' },
                borderRadius: 2.5,
                px: { xs: 2, sm: 2.5 },
                py: 0.75,
                fontWeight: 800,
                fontSize: '0.825rem',
                boxShadow: '0 4px 14px rgba(37, 99, 235, 0.35)',
                whiteSpace: 'nowrap',
              }}
            >
              {t('doneReordering')}
            </Button>

            {/* Reset Order Button */}
            <Button
              variant="outlined"
              color="inherit"
              size="small"
              startIcon={<RestartAltRoundedIcon />}
              onClick={handleResetOrder}
              sx={{
                borderRadius: 2.5,
                px: { xs: 1.5, sm: 2 },
                py: 0.75,
                fontWeight: 700,
                fontSize: '0.775rem',
                color: 'text.secondary',
                borderColor: (theme) =>
                  theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.15)' : 'rgba(0, 0, 0, 0.15)',
                whiteSpace: 'nowrap',
              }}
            >
              {t('resetOrder')}
            </Button>
          </Box>
        </Box>
      )}

      {/* Calculator Modal */}
      {currencies.length > 0 && (
        <ConverterModal
          open={converterOpen}
          onClose={() => setConverterOpen(false)}
          currencies={currencies}
          initialCurrency={selectedCurrency}
          t={t}
        />
      )}
    </Box>
  );
};

export default App;